import { CommonModule } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { Package } from '@app/akita/api/checkout/models/payment-order.model';
import { DeliveryStatus } from '@app/akita/api/deliveries/models/delivery-info.model';
import { Product } from '@app/akita/api/products/models/product.model';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { PopsySkeletonComponent } from '@app/components/skeleton/popsy-skeleton.component';
import { Check1IconComponent } from '@app/shared/components/icons/generated/check-1/check-1.component';
import { PictureComponent } from '@app/shared/components/picture/picture.component';
import { TranslocoModule } from '@ngneat/transloco';
import { Observable } from 'rxjs';

@Component({
  selector: 'popsy-order-tracking-status',
  templateUrl: './order-tracking-status.template.html',
  styleUrls: ['./order-tracking-status.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    TranslocoModule,
    PopsySkeletonComponent,
    PictureComponent,
    Check1IconComponent,
  ],
})
export class OrderTrackingStatusComponent implements OnChanges {
  @Input() public status: DeliveryStatus | string | null = null;
  @Input() public packages?: Array<Package> | null = null;
  @Input() public loading?: boolean | null = null;

  public biggerThan$: Observable<ScreenSizeInfoState>;

  public orderFailed?: boolean | null;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly akitaScreenQuery: AkitaScreenQuery
  ) {
    this.biggerThan$ = this.akitaScreenQuery.selectBiggerThan();

    this.refreshStatus();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.status) {
      this.refreshStatus();
      this.changeDetector.markForCheck();
    }
  }

  private refreshStatus(): void {
    this.orderFailed = this.getFailed();
  }

  public getOrderPlaced(status: string): boolean {
    return Boolean(
      status === 'UNCONFIRMED' ||
        status === 'CONFIRMED' ||
        status === 'PROCESSING' ||
        status === 'DELIVERED' ||
        status === 'DELIVERING' ||
        status === 'READY_TO_SHIP' ||
        status === 'IN_TRANSIT'
    );
  }

  public getOrderConfirmed(status: string): boolean {
    return Boolean(
      status === 'CONFIRMED' ||
        status === 'PROCESSING' ||
        status === 'DELIVERED' ||
        status === 'DELIVERING' ||
        status === 'READY_TO_SHIP' ||
        status === 'IN_TRANSIT'
    );
  }

  public getInDelivery(status: string): boolean {
    return Boolean(
      status === 'DELIVERED' || status === 'DELIVERING' || status === 'IN_TRANSIT'
    );
  }

  public getOrderInReturn(status: string): boolean {
    return Boolean(
      status === 'RETURN_IN_PROGRESS' ||
        status === 'RETURN_SHIPPED' ||
        status === 'RETURN_COMPLETED'
    );
  }

  public getInReturn(status: string): boolean {
    return Boolean(status === 'RETURN_IN_PROGRESS');
  }

  public getReturned(status: string): boolean {
    return Boolean(status === 'RETURN_SHIPPED' || status === 'RETURN_COMPLETED');
  }

  public getDelivered(status: string): boolean {
    return Boolean(status === 'DELIVERED');
  }

  public getPendingPayment(status: string): boolean {
    return Boolean(status === 'WAITING_PAYMENT');
  }

  public getFailed(): boolean {
    return Boolean(this.status === 'CANCELLED' || this.status === 'FAILED');
  }

  public getPackageImage(order: Array<Product>): string {
    return order[0].images[0];
  }

  public trackByPackage(index: number): string {
    return `${index}`;
  }
}
